import { type AnyAction, combineReducers } from 'redux';
import Immutable from 'immutable';

import { AssetContainerTypes, LicenseEnum, SlideBackgroundModelTypeEnum } from 'js/src/client';
import {
    immutableSlideBackgroundModelIsImmutableImageBackgroundModel,
    immutableSlideBackgroundModelIsImmutableVideoBackgroundModel,
} from 'js/src/data/media';
import StoreORM from 'js/src/data/store-orm';
import {
    addFiles,
    cancelAllUploads,
    clearFailedUploadMessages,
    clearUploads,
    createUploadSession,
    deleteUploadSession,
    endUploadExperience,
    finalizeAttributionData,
    markFileAsDeleted,
    markFileAsDeleting,
    markFileAsDeletionFailed,
    markFileAsFinalizing,
    removeUpload,
    setUploadFolder,
    startUploadExperience,
    storeCompletedFile,
    storeFailedUpload,
    storeSuccessfulUpload,
    storeUpdatedFileMetadata,
    storeUploadProgress,
    updateAttributionData,
    updateUploadedAssetMediaTags,
    updateUploadMediaTags,
    userCancelFile,
} from 'js/src/data/uploads/uploads-actions';
import type {
    FileUpload,
    UploadedAsset,
    UploadOptionsWithoutFileList,
    UploadSession,
} from 'js/src/data/uploads/uploads-types';
import {
    FILE_STATUS,
    immutableAssetContainerModelIsAudio,
    immutableAssetContainerModelIsBackground,
    immutableAssetContainerModelIsIcon,
} from 'js/src/data/uploads/uploads-types';
import { ImmutableFromJS, type ImmutableMap } from 'js/src/libs/consts';
import uuid from 'js/src/libs/uuid';

function find_file_id(file_obj: File, orm: StoreORM<FileUpload, string>): string | null {
    const results = orm.query({ key: ['file_obj'], value: file_obj });
    if (results.size === 1) {
        const id = results.get(0)!.get('id');
        return id;
    }
    return null;
}

export function upload_sessions(
    state: Immutable.Map<string, ImmutableMap<UploadSession>> = Immutable.Map(),
    action: AnyAction,
) {
    if (!action.payload?.uploadSessionId) {
        return state;
    }

    if (createUploadSession.match(action)) {
        // Use a different store prefix from the old files store
        // This is temporary until IO-1822 when we remove the old files store
        // TODO – can cleanup?
        const fileStore = StoreORM.newFromIdPrefix<FileUpload, string>('file');
        return state.set(
            action.payload.uploadSessionId,
            ImmutableFromJS({
                files: files(fileStore.data, action),
                // passing state = undefined to get default values
                files_can_be_edited: files_can_be_edited(undefined, action),
                folderId: folderId(undefined, action),
                attribution: attribution(undefined, action),
                media_tags: media_tags(undefined, action),
                license: license(undefined, action),
            }),
        );
    }
    if (deleteUploadSession.match(action)) {
        return state.delete(action.payload.uploadSessionId);
    }
    const uploadSession = state.get(action.payload.uploadSessionId);
    if (!uploadSession) {
        throw Error('attempting to change an upload session before it has been created');
    }
    if (startUploadExperience.match(action)) {
        return state.set(
            action.payload.uploadSessionId,
            uploadSession
                .set(
                    'files_can_be_edited',
                    files_can_be_edited(uploadSession.get('files_can_be_edited'), action),
                )
                .set('folderId', folderId(uploadSession.get('folderId'), action)),
        );
    }
    if (endUploadExperience.match(action)) {
        return state.set(
            action.payload.uploadSessionId,
            uploadSession
                .set(
                    'files_can_be_edited',
                    files_can_be_edited(uploadSession.get('files_can_be_edited'), action),
                )
                .set('folderId', folderId(uploadSession.get('folderId'), action)),
        );
    }
    if (
        cancelAllUploads.match(action) ||
        addFiles.match(action) ||
        storeSuccessfulUpload.match(action) ||
        updateUploadedAssetMediaTags.match(action) ||
        userCancelFile.match(action) ||
        markFileAsFinalizing.match(action) ||
        markFileAsDeleting.match(action) ||
        storeCompletedFile.match(action) ||
        storeUpdatedFileMetadata.match(action) ||
        markFileAsDeleted.match(action) ||
        markFileAsDeletionFailed.match(action) ||
        storeFailedUpload.match(action)
    ) {
        return state.set(
            action.payload.uploadSessionId,
            uploadSession.set('files', files(uploadSession.get('files'), action)),
        );
    }
    if (setUploadFolder.match(action)) {
        return state.set(
            action.payload.uploadSessionId,
            uploadSession.set('folderId', folderId(uploadSession.get('folderId'), action)),
        );
    }
    if (finalizeAttributionData.match(action) || updateAttributionData.match(action)) {
        return state.set(
            action.payload.uploadSessionId,
            uploadSession
                .set('attribution', attribution(uploadSession.get('attribution'), action))
                .set('license', license(uploadSession.get('license'), action)),
        );
    }
    if (updateUploadMediaTags.match(action)) {
        return state.set(
            action.payload.uploadSessionId,
            uploadSession.set('media_tags', media_tags(uploadSession.get('media_tags'), action)),
        );
    }
    return state;
}

export function files(
    state = StoreORM.newFromIdPrefix<FileUpload, string>('file').data,
    action: AnyAction,
) {
    if (cancelAllUploads.match(action)) {
        const orm = new StoreORM<FileUpload, string>(state);
        orm.updateAll({ key: ['user_canceled'], value: true });
        return orm.data;
    }
    if (addFiles.match(action)) {
        const orm = new StoreORM<FileUpload, string>(state);
        const upload_opts: UploadOptionsWithoutFileList = (({ file_list, ...rest }) => rest)(
            action.payload.opts,
        );
        action.payload.opts.file_list.forEach((file_obj) => {
            orm.pushJSItem(
                {
                    file_obj: file_obj.file,
                    file_metadata: Object.assign({}, file_obj.metadata),
                    upload_opts: upload_opts,
                    status: FILE_STATUS.LOADING,
                    user_canceled: false,
                    uploaded_asset: null,
                    fail_message: '',
                    tags: [],
                    id: uuid(),
                },
                'id',
            );
        });
        return orm.data;
    }
    if (storeSuccessfulUpload.match(action)) {
        const orm = new StoreORM<FileUpload, string>(state);
        const file_id = find_file_id(action.payload.file_obj, orm);
        if (!file_id) {
            throw Error('storeSuccessfulUpload: attempting to update a file that does not exist');
        }
        orm.updateById(file_id, {
            key: ['status'],
            value: FILE_STATUS.VALIDATED,
        });
        orm.updateById(file_id, {
            key: ['uploaded_asset'],
            value: action.payload.new_asset,
        });
        orm.updateById(file_id, {
            key: ['asyncOperationUuid'],
            value: action.payload.async_operation_uuid,
        });
        return orm.data;
    }
    if (updateUploadedAssetMediaTags.match(action)) {
        const orm = new StoreORM<FileUpload, string>(state);
        const file_id = find_file_id(action.payload.file_obj, orm);
        if (!file_id) {
            throw Error(
                'updateUploadedAssetMediaTags: attempting to update a file that does not exist',
            );
        }
        const previousTags = orm.getById(file_id).get('tags');
        const currentTags = Immutable.List(action.payload.tags);
        const updatedTags = Immutable.Set(previousTags.concat(currentTags));
        orm.updateById(file_id, { key: ['tags'], value: updatedTags });
        return orm.data;
    }
    if (userCancelFile.match(action)) {
        const orm = new StoreORM<FileUpload, string>(state);
        const file_id = find_file_id(action.payload.file_obj, orm);
        if (!file_id) {
            throw Error('userCancelFile: attempting to update a file that does not exist');
        }
        orm.updateById(file_id, { key: ['user_canceled'], value: true });
        return orm.data;
    }
    if (markFileAsFinalizing.match(action)) {
        const orm = new StoreORM<FileUpload, string>(state);
        orm.updateById(action.payload.file_id, {
            key: ['status'],
            value: FILE_STATUS.FINALIZING,
        });
        return orm.data;
    }
    if (markFileAsDeleting.match(action)) {
        const orm = new StoreORM<FileUpload, string>(state);
        orm.updateById(action.payload.file_id, {
            key: ['status'],
            value: FILE_STATUS.DELETING,
        });
        return orm.data;
    }
    if (storeCompletedFile.match(action)) {
        const orm = new StoreORM<FileUpload, string>(state);
        orm.updateById(action.payload.file_id, {
            key: ['status'],
            value: FILE_STATUS.COMPLETED,
        });
        orm.updateById(action.payload.file_id, {
            key: ['uploaded_asset'],
            value: Immutable.fromJS(action.payload.new_asset),
        });
        return orm.data;
    }
    if (storeUpdatedFileMetadata.match(action)) {
        const orm = new StoreORM<FileUpload, string>(state);
        const file_id = find_file_id(action.payload.file_with_metadata.file, orm);
        if (!file_id) {
            throw Error(
                'storeUpdatedFileMetadata: attempting to update a file that does not exist',
            );
        }
        const existing_metadata = orm.getById(file_id).get('file_metadata') || Immutable.Map();
        const mergedMetadata = {
            ...existing_metadata.toJS(),
            ...action.payload.file_with_metadata.metadata,
            ...action.payload.new_metadata,
        };
        orm.updateById(file_id, {
            key: ['file_metadata'],
            value: Immutable.fromJS(mergedMetadata),
        });
        return orm.data;
    }
    if (markFileAsDeleted.match(action)) {
        const orm = new StoreORM<FileUpload, string>(state);
        orm.updateById(action.payload.file_id, {
            key: ['status'],
            value: FILE_STATUS.DELETED,
        });
        return orm.data;
    }
    if (markFileAsDeletionFailed.match(action)) {
        const orm = new StoreORM<FileUpload, string>(state);
        orm.updateById(action.payload.file_id, {
            key: ['status'],
            value: FILE_STATUS.DELETE_FAILED,
        });
        return orm.data;
    }
    if (storeFailedUpload.match(action)) {
        const orm = new StoreORM<FileUpload, string>(state);
        const file_id = find_file_id(action.payload.file_obj, orm);
        if (!file_id) {
            throw Error('storeFailedUpload: attempting to update a file that does not exist');
        }
        orm.updateById(file_id, { key: ['status'], value: FILE_STATUS.FAILED });
        orm.updateById(file_id, {
            key: ['fail_message'],
            value: action.payload.message,
        });
        return orm.data;
    }
    return state;
}

export function files_can_be_edited(state = false, action: AnyAction) {
    if (startUploadExperience.match(action)) {
        return true;
    }
    if (endUploadExperience.match(action)) {
        return false;
    }
    return state;
}

export function progress(
    state: Immutable.Map<string, number> = Immutable.Map(),
    action: AnyAction,
) {
    if (storeUploadProgress.match(action)) {
        if (action.payload.progress < 0) {
            return state.delete(action.payload.session_id);
        }
        return state.set(action.payload.session_id, action.payload.progress);
    }
    return state;
}

export function folderId(state: number | null = null, action: AnyAction) {
    if (startUploadExperience.match(action)) {
        return null;
    }
    if (endUploadExperience.match(action) || setUploadFolder.match(action)) {
        return action.payload.folderId;
    }
    return state;
}

export function attribution(state = '', action: AnyAction) {
    if (finalizeAttributionData.match(action) || updateAttributionData.match(action)) {
        return action.payload.attribution;
    }
    return state;
}

export function media_tags(state = Immutable.List<string>(), action: AnyAction) {
    if (updateUploadMediaTags.match(action)) {
        return Immutable.List(action.payload.mediaTags);
    }
    return state;
}

export function license(state = LicenseEnum.USER_PRIVATE, action: AnyAction) {
    if (finalizeAttributionData.match(action)) {
        return action.payload.license;
    }
    return state;
}

export function uploaded_assets(
    state = StoreORM.newFromIdPrefix<UploadedAsset>('upload').data,
    action: AnyAction,
) {
    if (storeCompletedFile.match(action)) {
        const orm = new StoreORM(state);
        // font and document uploads should never call this action
        if (
            action.payload.new_asset_type === AssetContainerTypes.FONT ||
            action.payload.new_asset_type === AssetContainerTypes.DOCUMENT
        ) {
            throw Error('storeCompletedFile: attempting to store a font or document upload');
        }
        orm.pushJSItem({
            type: action.payload.new_asset_type,
            asset: action.payload.new_asset,
        } as UploadedAsset);
        return orm.data;
    }
    if (removeUpload.match(action)) {
        const orm = new StoreORM(state);
        let assetContainerType: AssetContainerTypes;
        let uuid: string;
        if (immutableAssetContainerModelIsBackground(action.payload.asset)) {
            let slideBackgroundType: SlideBackgroundModelTypeEnum;
            const background = action.payload.asset.get(AssetContainerTypes.BACKGROUND);
            if (immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(background)) {
                slideBackgroundType = SlideBackgroundModelTypeEnum.VIDEO;
                uuid = background.get(slideBackgroundType).get('uuid')!;
            } else if (immutableSlideBackgroundModelIsImmutableImageBackgroundModel(background)) {
                slideBackgroundType = SlideBackgroundModelTypeEnum.IMAGE;
                uuid = background.get(slideBackgroundType).get('uuid')!;
            } else {
                throw new Error(
                    'removeUpload: attempting to remove a background that is not supported',
                );
            }

            const ids = orm.queryIds({
                key: ['asset', slideBackgroundType, 'uuid'],
                value: uuid,
            });
            if (ids.size === 1) {
                orm.delete(ids);
            }
        } else if (immutableAssetContainerModelIsAudio(action.payload.asset)) {
            assetContainerType = AssetContainerTypes.AUDIO;
            const ids = orm.queryIds({
                key: ['asset', 'uuid'],
                value: action.payload.asset.get(assetContainerType).get('uuid'),
            });
            if (ids.size === 1) {
                orm.delete(ids);
            }
        } else if (immutableAssetContainerModelIsIcon(action.payload.asset)) {
            assetContainerType = AssetContainerTypes.ICON;
            const ids = orm.queryIds({
                key: ['asset', 'uuid'],
                value: action.payload.asset.get(assetContainerType).get('uuid'),
            });
            if (ids.size === 1) {
                orm.delete(ids);
            }
        } else {
            throw Error('removeUpload: attempting to remove an asset that is not supported');
        }
        return orm.data;
    }
    if (clearUploads.match(action)) {
        return StoreORM.newFromIdPrefix<UploadedAsset>('upload').data;
    }
    return state;
}

export function fail_messages(
    state: Immutable.List<ImmutableMap<{ name: string; message: string }>> = Immutable.List(),
    action: AnyAction,
) {
    if (storeFailedUpload.match(action)) {
        return state.push(
            ImmutableFromJS({
                name: action.payload.file_obj.name,
                message: action.payload.message,
            }),
        );
    }
    if (clearFailedUploadMessages.match(action)) {
        return state.clear();
    }
    return state;
}

export const uploads = combineReducers({
    upload_sessions,
    fail_messages,
    progress,
    uploaded_assets,
});

export type UploadsState = ReturnType<typeof uploads>;
